import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import updateLocale from "dayjs/plugin/updateLocale"
import { extractParents, getLocale, localizeDateTime, toFacilityTime } from "."
import { AlertThresholdResponse, ThresholdType } from "@/api/AlertsService"
import { AlertModalType } from "@/components/AlertModal"
import {
   noSoundFile,
   SoundFile,
   soundFiles,
} from "@/components/AlertSoundSettings/files"
import { AlertModalData } from "@/components/shared/Header"
import {
   Alert,
   AlertLevel,
   AlertSound,
   AlertSource,
   AlertThreshold,
   AlertVariant,
   TypefaceVariant,
} from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { get } from "@/utils/lodash"

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)

interface AlarmContext {
   source?: AlertSource
   hasDoorSensor?: boolean
}

export function normalizeAlertLevelParents(level: AlertLevel): AlertLevel {
   const parents = level.parents ?? []
   return {
      ...level,
      parents: parents.map((parent) => normalizeAlertLevelParents(parent)),
   }
}

export function sortAlerts(a: Alert, b: Alert) {
   if (b.priority !== a.priority) {
      return a.priority - b.priority
   }
   return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
}

export function managePlayingAlertSound(
   alerts: Alert[],
   alertsToPlay: Alert[]
): Alert[] {
   const currentlyPlaying = alerts.find((a) => a.soundPlaying)

   const highestPriority = alertsToPlay.reduce((prev, current) =>
      current.priority < prev.priority ? current : prev
   )

   if (
      !currentlyPlaying ||
      highestPriority.priority < currentlyPlaying.priority
   ) {
      return alerts.map((alert) => ({
         ...alert,
         soundPlaying: alert.id === highestPriority.id,
      }))
   } else {
      return alerts
   }
}

export function isAlertSoundMuted(alert: Alert): boolean {
   return alert.alertConfiguration.sound.clip === 0
}

export function getAlertSound(alertSound: AlertSound): SoundFile {
   const finding = soundFiles.find((file) => file.id === alertSound.clip)
   return finding || noSoundFile
}

export function relationalTime(dateTime: string, timezone: string | null) {
   if (!dateTime) return

   const locale = getLocale()
   const now = toFacilityTime(dayjs(), timezone)
   const event = toFacilityTime(dayjs(dateTime), timezone)

   if (now.isSame(event, "day")) {
      let diffSeconds = now.diff(event, "second")

      // Prevents showing negative values if the event is in the future
      if (diffSeconds < 0) diffSeconds = 0

      const lessThanHour = diffSeconds < 3600

      if (lessThanHour) {
         const minutes = Math.floor(diffSeconds / 60)
            .toString()
            .padStart(2, "0")
         const seconds = (diffSeconds % 60).toString().padStart(2, "0")
         return `${minutes}:${seconds} ago`
      } else {
         const isUS = locale.startsWith("en-US")
         return event.format(isUS ? "hh:mm A" : "HH:mm")
      }
   } else {
      return localizeDateTime(event, timezone)
   }
}

function mapThreshold(threshold: AlertThresholdResponse): AlertThreshold {
   return {
      high: threshold.high.value,
      low: threshold.low.value,
      highThresholdDuration: threshold.high.duration,
      lowThresholdDuration: threshold.low.duration,
      processingMethod: threshold.processingMethod,
   }
}

export function processThresholds(thresholds: AlertThresholdResponse[]): {
   heartRateThreshold?: AlertThreshold
   respiratoryRateThreshold?: AlertThreshold
} {
   return thresholds.reduce(
      (acc, threshold) => {
         if (threshold.type === ThresholdType.HEART_RATE) {
            acc.heartRateThreshold = mapThreshold(threshold)
         }
         if (threshold.type === ThresholdType.RESPIRATORY_RATE) {
            acc.respiratoryRateThreshold = mapThreshold(threshold)
         }
         return acc
      },
      {} as {
         heartRateThreshold?: AlertThreshold
         respiratoryRateThreshold?: AlertThreshold
      }
   )
}

export function getAlarmDisplayName(
   name: string,
   systemConditionType: AlertSystemConditionType | null | undefined,
   { source, hasDoorSensor }: AlarmContext = {}
): string {
   switch (systemConditionType) {
      case AlertSystemConditionType.BROKEN_WEARABLE:
         return "Tamper Alarm"
      case AlertSystemConditionType.OUT_OF_BOUNDS:
         return "Out Of Bounds"
      case AlertSystemConditionType.NO_VITALS: {
         const canBeEmptyCell =
            source !== AlertSource.WEARABLE && !hasDoorSensor
         return canBeEmptyCell ? "No Vitals / Empty Cell" : "No Vitals"
      }
      default:
         return name
   }
}

export const prepareAlertModalData = (
   alert: Alert | undefined
): AlertModalData => {
   if (!alert) {
      return {} as AlertModalData
   }

   const isWearableAlert = alert.source === AlertSource.WEARABLE

   return {
      dateTime: alert.timestamp,
      id: alert.id,
      systemConditionType:
         alert.alertConfiguration.systemCondition?.type ?? null,
      alertType: isWearableAlert
         ? AlertModalType.WEARABLE
         : AlertModalType.CELL,
      levels: extractParents(get(alert, "level.parents", [])),
      locationName: get(alert, "level.name", ""),
      locationType: get(alert, "level.type.name", ""),
      locationId: get(alert, "level.id", ""),
      isCustom: get(alert, "alertConfiguration.custom", false),
      alertVariant:
         alert.alertConfiguration.color.card.toLowerCase() as keyof typeof AlertVariant,
      typefaceColor:
         alert.alertConfiguration.color.typeface.toLowerCase() as keyof typeof TypefaceVariant,
      alertName: getAlarmDisplayName(
         alert.alertConfiguration.name,
         alert.alertConfiguration.systemCondition?.type,
         {
            source: alert.source,
            hasDoorSensor: get(alert, "level.hasDoorSensor"),
         }
      ),
      vitals: alert.vitals,
      heartRateThreshold: alert.heartRateThreshold,
      respiratoryRateThreshold: alert.respiratoryRateThreshold,
      inmateId: isWearableAlert ? get(alert, "inmate.id", "") : undefined,
   }
}
